import { clockOffsetMs, dismissKey, parseStatus, visibleFor } from './maintenance-state';
import type { Shown, StatusFile, StatusWindow } from './maintenance-state';

export type MaintenanceSnapshot = { shown: Shown[]; productNames: Record<string, string> };

export type MaintenanceClientOptions = {
  url: string;
  product: string;
  region: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
};

export type MaintenanceClient = {
  start: () => void;
  stop: () => void;
  subscribe: (fn: () => void) => () => void;
  getSnapshot: () => MaintenanceSnapshot;
  dismiss: (w: StatusWindow) => void;
  requestCheck: () => void;
  isPreview: () => boolean;
  currentLive: () => Shown | null;
  serverNow: () => number;
};

const POLL_MS = 60000;
const MIN_MS = 60000;
const MIN_BOUNDARY_GAP_MS = 250;
const MIN_EVENT_GAP_MS = 5000;
const CHECK_EVENT = 'maintenance:check';
const DISMISSED_KEY = 'maintenance.dismissed';
const PREVIEW_KEY = 'maintenance.preview';
const EMPTY: MaintenanceSnapshot = { shown: [], productNames: {} };

/** Earliest time after `now` at which any window changes phase (notice, soon, start, end, back over). */
function nextBoundary(file: StatusFile, now: number): number {
  let next = Infinity;
  file.windows.forEach((w) => {
    const start = Date.parse(w.startsAt);
    const end = Date.parse(w.endedAt !== null ? w.endedAt : w.endsAt || '');
    [start - w.noticeMinutes * MIN_MS, start - 15 * MIN_MS, start, end, end + 15 * MIN_MS].forEach((t) => {
      if (t > now && t < next) next = t;
    });
  });
  return next;
}

function readDismissed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.filter((x: unknown) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeDismissed(keys: Set<string>) {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    // Storage blocked: the dismissal lasts for this page view only.
  }
}

function previewFlag(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('maintenancePreview') === '1') {
      window.sessionStorage.setItem(PREVIEW_KEY, '1');
    }
    return window.sessionStorage.getItem(PREVIEW_KEY) === '1';
  } catch {
    return false;
  }
}

export function createMaintenanceClient(opts: MaintenanceClientOptions): MaintenanceClient {
  const enabled = opts.url.trim() !== '';
  const clock = opts.now || (() => Date.now());
  const listeners = new Set<() => void>();
  let file: StatusFile | null = null;
  let offset = 0;
  let dismissed: Set<string> = new Set();
  let snapshot: MaintenanceSnapshot = EMPTY;
  let signature = '';
  let timer: ReturnType<typeof setInterval> | null = null;
  let boundaryTimer: ReturnType<typeof setTimeout> | null = null;
  let warned = false;
  let running = false;
  let inflight: Promise<void> | null = null;
  let lastFetchAt = -Infinity;

  function clearBoundary() {
    if (boundaryTimer !== null) clearTimeout(boundaryTimer);
    boundaryTimer = null;
  }

  function recompute() {
    if (!file) return;
    const now = clock() + offset;
    clearBoundary();
    const next = nextBoundary(file, now);
    if (running && next !== Infinity) {
      const wait = Math.min(POLL_MS, Math.max(MIN_BOUNDARY_GAP_MS, next - now));
      boundaryTimer = setTimeout(recompute, wait);
    }
    const shown = visibleFor(file, opts.product, opts.region, now, dismissed);
    const sig =
      shown.map((s) => `${dismissKey(s.window)}:${s.phase}`).join('|') + JSON.stringify(file.productNames);
    if (sig === signature && snapshot !== EMPTY) return;
    signature = sig;
    snapshot = { shown, productNames: file.productNames };
    listeners.forEach((fn) => fn());
  }

  async function fetchOnce() {
    try {
      const doFetch = opts.fetchImpl || fetch;
      const res = await doFetch(opts.url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const parsed = parseStatus(await res.json());
      if (!running) return;
      if (!parsed) throw new Error('unrecognised status file');
      offset = clockOffsetMs(res.headers.get('Date'), clock());
      file = parsed;
      recompute();
    } catch (e) {
      if (running && !warned) {
        warned = true;
        console.warn('Maintenance status unavailable', e);
      }
    }
  }

  function check(fromEvent: boolean): Promise<void> {
    if (!enabled || !running) return Promise.resolve();
    if (inflight) return inflight;
    if (fromEvent && clock() - lastFetchAt < MIN_EVENT_GAP_MS) return Promise.resolve();
    lastFetchAt = clock();
    inflight = fetchOnce().then(() => {
      inflight = null;
    });
    return inflight;
  }

  function onVisible() {
    if (document.visibilityState === 'visible') void check(true);
  }
  function onCheck() {
    void check(true);
  }

  return {
    start() {
      if (!enabled || running || typeof window === 'undefined') return;
      running = true;
      dismissed = readDismissed();
      void check(false);
      timer = setInterval(() => {
        void check(false);
        recompute();
      }, POLL_MS);
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener(CHECK_EVENT, onCheck);
    },
    stop() {
      running = false;
      if (timer !== null) clearInterval(timer);
      timer = null;
      clearBoundary();
      if (typeof window === 'undefined') return;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(CHECK_EVENT, onCheck);
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    getSnapshot: () => snapshot,
    serverNow: () => clock() + offset,
    dismiss(w) {
      dismissed = new Set(dismissed);
      dismissed.add(dismissKey(w));
      writeDismissed(dismissed);
      recompute();
    },
    requestCheck() {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHECK_EVENT));
    },
    isPreview: () => typeof window !== 'undefined' && previewFlag(),
    currentLive() {
      for (const s of snapshot.shown) if (s.phase === 'live') return s;
      return null;
    },
  };
}
